import React, { useEffect, useState } from 'react';
import { getApiUrl } from '../config/api';

const placeholderTickers = [
  { symbol: 'NIFTY 50', value: 'Connecting', change: '', pct: '', up: true, source: 'placeholder' },
  { symbol: 'SENSEX', value: 'Connecting', change: '', pct: '', up: true, source: 'placeholder' },
  { symbol: 'BANK NIFTY', value: 'Connecting', change: '', pct: '', up: true, source: 'placeholder' },
  { symbol: 'NIFTY IT', value: 'Connecting', change: '', pct: '', up: true, source: 'placeholder' },
  { symbol: 'NIFTY AUTO', value: 'Connecting', change: '', pct: '', up: true, source: 'placeholder' },
  { symbol: 'USD/INR', value: 'Connecting', change: '', pct: '', up: true, source: 'placeholder' },
  { symbol: 'GOLD (INR)', value: 'Connecting', change: '', pct: '', up: true, source: 'placeholder' },
  { symbol: 'INDIA VIX', value: 'Connecting', change: '', pct: '', up: true, source: 'placeholder' },
];

const placeholderBySymbol = new Map(placeholderTickers.map((ticker) => [ticker.symbol, ticker]));
const MARKET_TICKER_REFRESH_MS = Math.max(
  15000,
  Number(process.env.REACT_APP_MARKET_TICKER_REFRESH_MS) || 60000,
);

const isRealSource = (source) => {
  const normalized = String(source || '').toLowerCase();
  return Boolean(normalized && normalized !== 'fallback' && normalized !== 'placeholder');
};

const normalizeTickers = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return placeholderTickers;
  }

  const normalizedBySymbol = new Map();

  items.forEach((item) => {
    const symbol = item.symbol || '';

    if (!symbol) {
      return;
    }

    if (!isRealSource(item.source)) {
      normalizedBySymbol.set(symbol, placeholderBySymbol.get(symbol) || {
        symbol,
        value: 'Connecting',
        change: '',
        pct: '',
        up: true,
        source: 'placeholder',
      });
      return;
    }

    normalizedBySymbol.set(symbol, {
      symbol,
      value: item.value || '—',
      change: item.change || '',
      pct: item.pct || '',
      up: item.up !== false,
      source: item.source || '',
      marketTime: item.marketTime || '',
    });
  });

  return placeholderTickers.map((ticker) => normalizedBySymbol.get(ticker.symbol) || ticker);
};

const TickerItem = ({ t }) => (
  <span className="inline-flex w-max min-w-[255px] shrink-0 items-center gap-4 px-6 text-[13px] font-black tracking-tight">
    <span className="shrink-0 text-[#0b1830] uppercase">{t.symbol}</span>
    <span className="shrink-0 text-[#0b1830] font-black">{t.value}</span>
    {(t.change || t.pct) && (
      <span className={`shrink-0 ${t.up ? 'text-emerald-700 font-black' : 'text-rose-700 font-black'}`}>
        {t.up ? '▲' : '▼'} {t.change}{t.pct && <span className="ml-1 opacity-90">({t.pct})</span>}
      </span>
    )}
  </span>
);

const MarketTicker = () => {
  const [tickers, setTickers] = useState(placeholderTickers);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    let active = true;
    let inFlight = false;
    let controller = null;

    const loadTickers = async () => {
      if (document.hidden) {
        return;
      }

      if (inFlight) {
        return;
      }

      inFlight = true;
      controller = new AbortController();

      try {
        const API = getApiUrl();
        const resp = await fetch(`${API}/market/tickers`, {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal,
        });

        if (!resp.ok) {
          throw new Error(`Market ticker request failed with ${resp.status}`);
        }

        const data = await resp.json();
        if (active) {
          const nextTickers = normalizeTickers(data?.tickers);
          const hasRealSource = nextTickers.some((ticker) => isRealSource(ticker.source));
          setTickers(nextTickers);
          setConnected(hasRealSource);
        }
      } catch (e) {
        if (e.name !== 'AbortError' && active) {
          setTickers(placeholderTickers);
          setConnected(false);
        }
      } finally {
        inFlight = false;
      }
    };

    loadTickers();
    const interval = window.setInterval(loadTickers, MARKET_TICKER_REFRESH_MS);
    document.addEventListener('visibilitychange', loadTickers);

    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', loadTickers);
      controller?.abort();
    };
  }, []);

  const renderTrack = (suffix = '') => (
    <div className="flex shrink-0 whitespace-nowrap">
      {tickers.map((t) => <TickerItem key={`${t.symbol}${suffix}`} t={t} />)}
    </div>
  );

  return (
    <div
      className="fixed top-[5rem] left-0 right-0 z-40 bg-[#fffdf9] border-b border-[#e1e7ef] overflow-hidden shadow-sm"
      data-testid="market-ticker"
    >
      <div className="flex items-center min-w-0 h-[46px]">
        <div className="flex-shrink-0 px-4 py-2 bg-[#0b1830] text-white text-[11px] font-black tracking-[0.14em] uppercase">
          <span className="inline-flex items-center gap-2">
            <span className={`h-1.5 w-1.5 rounded-full ${connected ? 'bg-[#C8A84B] animate-pulse' : 'bg-slate-400'}`} />
            {connected ? 'Live' : 'Connecting'}
          </span>
        </div>
        <div className="relative flex-1 min-w-0 overflow-hidden py-2">
          <div className="flex w-max min-w-full animate-ticker">
            {renderTrack()}
            {renderTrack('-copy')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketTicker;
